import React from "react";
import { Box, Button, Flex, Heading, HStack, Spacer } from "@chakra-ui/react";
import { IoTrashBinOutline } from "react-icons/io5";
import { MedicationCalculationTypeEnum, RegionalBlockTypeEnum } from "requests";
import Modal from "../../modal";
import { Card } from "./components";

interface MedicationGroupProps {
  groupName: string;
  medications: any[];
  create?: React.ReactElement;
  onDelete?(ids: number[]): Promise<any> | void;
  allowCardToggle?: boolean;
  expandedCards?: boolean;
  doseUnit?: string;
  viewOnly?: boolean;
  hasTimeAdministered?: boolean;
  hasAdditionalVolumeToBeDrawn?: boolean;
  medicationType:
    | MedicationCalculationTypeEnum
    | RegionalBlockTypeEnum
    | "tablet"
    | "suspension"
    | "defib"
    | "fluidBolus";
}

const MedicationGroup: React.FC<MedicationGroupProps> = ({
  groupName,
  medications,
  create,
  onDelete,
  allowCardToggle = true,
  expandedCards = true,
  doseUnit = "mg",
  viewOnly = false,
  medicationType,
  hasTimeAdministered = true,
  hasAdditionalVolumeToBeDrawn = true
}) => {
  const [selectedMedications, setSelectedMedications] = React.useState<
    { id: number; name: string }[]
  >([]);

  return (
    <Box>
      <HStack spacing={4}>
        <Heading as="h3" color="black" fontSize="lg" lineHeight="7">
          {groupName}
        </Heading>
        <Spacer />
        {onDelete && medications.length && (
          <Modal.Confirm
            title="Remove Medication"
            body="Are you sure you want to remove? You can’t undo this action afterwards."
            confirmText="Remove"
            successMessage={
              selectedMedications.length > 1
                ? `Medications ${selectedMedications
                    .map(m => m.name)
                    .join(", ")} are deleted successfully.`
                : `Medication ${selectedMedications[0]?.name} is deleted successfully.`
            }
            onConfirm={() =>
              Promise.resolve(
                onDelete(selectedMedications.map(m => m.id))
              ).then(() => {
                setSelectedMedications([]);
              })
            }
          >
            <Button
              disabled={!selectedMedications.length}
              colorScheme="red"
              leftIcon={<IoTrashBinOutline />}
            >
              Remove
            </Button>
          </Modal.Confirm>
        )}
        {create}
      </HStack>
      <Flex mt={medications.length ? 4 : 0} direction="column" rowGap={4}>
        {medications.map(med => {
          const { medication, _id, fakeId, ...data } = med;
          const id = fakeId || _id;

          const handleSelect = () => {
            const selectedMeds = selectedMedications.some(m => m.id === id)
              ? selectedMedications.filter(m => m.id !== id)
              : [...selectedMedications, { id, name: medication }];

            setSelectedMedications(selectedMeds);
          };

          return (
            <Card
              key={id}
              id={id}
              name={medication}
              data={data}
              isSelected={
                onDelete
                  ? selectedMedications.some(m => m.id === id)
                  : undefined
              }
              onSelect={onDelete ? handleSelect : undefined}
              allowToggle={allowCardToggle}
              expanded={expandedCards}
              doseUnit={doseUnit}
              viewOnly={viewOnly}
              medicationType={medicationType}
              hasTimeAdministered={hasTimeAdministered}
              hasAdditionalVolumeToBeDrawn={hasAdditionalVolumeToBeDrawn}
            />
          );
        })}
      </Flex>
    </Box>
  );
};

export default MedicationGroup;
